/* eslint-disable max-len */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { Sticky } from '@thd-olt-component-react/sticky';
import classNames from 'classnames';
import { DraggableDrawer } from './drawer/DraggableDrawer';
import { getPortalRootNode } from '../shared/utils/assistant-utils';
import { useZIndexManager } from '../hooks/useZIndexManager';
import './DrawerFrame.scss';

// DrawerFrame is the assistant's PLACEMENT, and nothing else: where the drawer sits on the
// page and how it stacks, for whatever content is handed to it. It owns the responsive
// mobile/desktop split, the portal target, the sticky wrapper and mobile scrim, the
// host-page root grid class, and the LiveChat z-index reordering.
//
// It is deliberately CONTENT-AGNOSTIC — no orchestrator, no store, no journey, no drawer
// context, no header. Everything it renders arrives as `children`. That is the whole point:
// the frame is the one part of the drawer a second content implementation genuinely wants,
// because placement is a property of the host page rather than of the conversation. This
// was carved out of AssistantShell, which had grown to own both the frame and the body
// (the TODO in 2b0b79a); AssistantShell now composes this with the refactored body, and a
// legacy content tree can compose it with its own.
//
// Prop names match the retired `ResponsiveAssistant` (`drawerState` / `setDrawerState` /
// `offsetMargin` / `children`) because this is its direct descendant, and the legacy tree
// that used to render it can be ported onto this without a translation layer.
//
// Providers and error boundaries belong ABOVE this component, not inside it. Both drawer
// context and journey context must survive a mobile↔desktop breakpoint flip; the two
// responsive branches are different subtree positions, so a provider mounted inside the
// frame would be torn down and rebuilt (losing scroll state and the resolved journey) every
// time the viewport crossed `lg`.
export const DrawerFrame = ({
  drawerState, setDrawerState, offsetMargin, children,
}) => {
  const { lesser: isMobile } = useBreakpoint('lg');
  const portalRootNode = getPortalRootNode();

  useEffect(() => {
    const rootEl = document.getElementById('root');
    // Add/remove CSS class based on current breakpoint
    if (!isMobile && rootEl) {
      rootEl.classList.add('magic-apron-assistant-root');
    } else if (rootEl) {
      rootEl.classList.remove('magic-apron-assistant-root');
    }

    return () => {
      if (!isMobile && rootEl) {
        rootEl.classList.remove('magic-apron-assistant-root');
      }
    };
  }, [isMobile]);

  // Manage z-index of LiveChat elements when drawer state is OPEN
  useZIndexManager('#spr-live-chat-app > div', 900, drawerState === 'OPEN');

  const stickyClassnames = classNames('sui-w-full sui-left-0 sui-transition-[bottom]', {
    '!sui-duration-500': offsetMargin > 0
  });

  if (isMobile) {
    return (
      // StickyWithHeaderObserver has z-index of 899 that we can't override so need to wrap to be on top of header/footer
      <div
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        className="sui-z-[998] sui-relative"
        data-testid="magic-apron-assistant"
        data-component="DrawerFrame"
      >
        {/* Scrim only. The opaque filler that used to sit here at a fixed `sui-h-2/3` moved
            into DraggableDrawer, where it is sized from `dynamicBottom` — the fixed two
            thirds did not match the gap an Android soft keyboard actually leaves. */}
        {drawerState === 'OPEN' && <div className="sui-fixed sui-inset-0 sui-bg-inverse/50" />}
        <Sticky
          className={stickyClassnames}
          position="bottom"
          stickyByDefault
          visibleByDefault
          offsetMargin={offsetMargin}
        >
          <DraggableDrawer
            drawerState={drawerState}
            setDrawerState={setDrawerState}
          >
            {children}
          </DraggableDrawer>
        </Sticky>
      </div>
    );
  }

  // Built once and either portaled or rendered in place — the two desktop branches differed
  // only in the createPortal call, so the markup is no longer duplicated between them.
  const aside = (
    <aside
      id="magic-apron-assistant-drawer"
      data-testid="magic-apron-assistant"
      data-component="DrawerFrame"
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      className="sui-sticky sui-flex sui-flex-col sui-h-[calc(100vh-169px)] sui-top-[169px] sui-ml-3 sui-mr-8 sui-border-solid sui-border-1 sui-border-primary"
    >
      {children}
    </aside>
  );

  return portalRootNode !== null ? createPortal(aside, portalRootNode) : aside;
};

DrawerFrame.propTypes = {
  drawerState: PropTypes.oneOf(['OPEN', 'MINIMIZED', 'CLOSED']).isRequired,
  setDrawerState: PropTypes.func.isRequired,
  offsetMargin: PropTypes.number.isRequired,
  children: PropTypes.node,
};
